import { useCallback, useEffect, useRef, useState } from 'react';

// At most this many cover fetches run at once. Each cover is a listContributions
// call plus an image download, so firing them all together saturates the
// network and everything crawls. Tune per backend/connection.
const MAX_CONCURRENT = 3;

const CLOUDFRONT_BASE = 'https://d1lnmqwvmebyqc.cloudfront.net/';

// Pull the first usable thumbnail out of a folder's contributions.
// Video  -> payload.thumbnail_signed (fallback: first of thumbnails_signed)
// Album  -> payload.icon_signed (fallback: payload.icon)
// Returns '' when no contribution has a usable thumbnail.
const extractCover = (contributions) => {
  if (!Array.isArray(contributions)) return '';
  for (let i = 0; i < contributions.length; i += 1) {
    const contrib = contributions[i];
    const p = contrib?.payload?.payload;
    if (p) {
      if (contrib.type === 'album') {
        if (p.icon_signed) return p.icon_signed;
        if (p.icon) return p.icon.startsWith('http') ? p.icon : `${CLOUDFRONT_BASE}${p.icon}`;
      } else {
        if (p.thumbnail_signed) return p.thumbnail_signed;
        if (Array.isArray(p.thumbnails_signed) && p.thumbnails_signed[0]) {
          return p.thumbnails_signed[0];
        }
      }
    }
  }
  return '';
};

/**
 * Derive a preview cover image for each shared folder on the client, with zero
 * backend changes. The folder-list API returns only text, so for every folder
 * with contributions we steal the first available thumbnail from its contents.
 *
 * React-style: covers live in component state (uuid -> url) so cards re-render
 * as covers resolve; a ref-backed queue throttles concurrency; the presence of
 * a uuid key (even '' meaning "no cover") marks it resolved so it is never
 * re-fetched within the session.
 *
 * @param {Array}    links               folder list (needs uuid + contribution_count)
 * @param {Function} fetchContributions  async (uuid) => contributions[]
 * @returns {{ covers: Object, invalidateCover: Function }}
 */
export default function useFolderCovers(links, fetchContributions) {
  // uuid -> cover url. '' means "resolved, no usable cover".
  const [covers, setCovers] = useState({});
  // uuids already resolved OR in flight OR queued — the dedupe/cache gate.
  const seenRef = useRef(new Set());
  const queueRef = useRef([]);
  const activeRef = useRef(0);
  const mountedRef = useRef(true);
  const fetchRef = useRef(fetchContributions);

  // Keep the latest fetcher without making pump()/effects depend on its
  // identity (parent may pass a fresh closure each render).
  fetchRef.current = fetchContributions;

  useEffect(() => () => { mountedRef.current = false; }, []);

  // Drain the queue up to MAX_CONCURRENT in-flight requests. Each completion
  // pumps the next one, so covers pop in progressively.
  const pump = useCallback(() => {
    while (activeRef.current < MAX_CONCURRENT && queueRef.current.length > 0) {
      const uuid = queueRef.current.shift();
      activeRef.current += 1;
      Promise.resolve()
        .then(() => fetchRef.current(uuid))
        .then((contributions) => {
          if (!mountedRef.current) return;
          setCovers((prev) => ({ ...prev, [uuid]: extractCover(contributions) }));
        })
        .catch(() => {
          if (!mountedRef.current) return;
          setCovers((prev) => ({ ...prev, [uuid]: '' }));
        })
        .finally(() => {
          activeRef.current -= 1;
          pump();
        });
    }
  }, []);

  // Queue any not-yet-seen folder that actually has contributions. Folders with
  // contribution_count === 0 are skipped entirely (icon only).
  useEffect(() => {
    if (!Array.isArray(links) || links.length === 0) return;
    let added = false;
    links.forEach((link) => {
      const uuid = link?.uuid;
      const count = Number(link?.contribution_count) || 0;
      if (!uuid || count <= 0 || seenRef.current.has(uuid)) return;
      seenRef.current.add(uuid);
      queueRef.current.push(uuid);
      added = true;
    });
    if (added) pump();
  }, [links, pump]);

  // Drop a folder's cached cover so it re-resolves — used on background refresh
  // when a folder's contribution_count changed (its signed URL may be stale or
  // its first item may have changed).
  const invalidateCover = useCallback((uuid) => {
    if (!uuid) return;
    seenRef.current.delete(uuid);
    setCovers((prev) => {
      if (!(uuid in prev)) return prev;
      const next = { ...prev };
      delete next[uuid];
      return next;
    });
  }, []);

  return { covers, invalidateCover };
}
